// Phase 2 — "By Partner" tab. Owns its own filter bar, KPI strip, partner
// summary table, manual-adjustments section + modal, and the per-period
// breakdown. Click handlers (saveAdj, removeAdj, openBreakdown, etc.) are
// owned by the parent so server state mutations stay centralised.

import { Section, Badge, Btn, Select, Modal, Input, MonthPicker } from "@/components/Shared";
import { Plus, Pencil, Trash2, Lock, Unlock, RefreshCw } from "lucide-react";
import { fmtINR, fmtPeriod } from "@/lib/api";

// 5-state status badge — derives tone from status string. Mirrors the
// backend status values: "Paid" | "Partially Paid" | "Locked" |
// "Partially Locked" | "Drift" | "Payable" | "No Payout". Single source
// of truth so the by-partner and by-period tables stay visually consistent.
function PayoutStatusBadge({ status }) {
  const tone = (
    status === "Paid" ? "positive"
    : status === "Partially Paid" ? "warning"
    : status === "Locked" ? "default"
    : status === "Partially Locked" ? "default"
    : status === "Drift" ? "warning"
    : status === "Payable" ? "accent"
    : "muted"
  );
  const label = status === "Drift" ? "DRIFT ⚠" : status;
  return <Badge tone={tone}>{label}</Badge>;
}

export default function PayoutsByPartnerTab({
  data,
  period,
  partners,
  partnerTabFilter, setPartnerTabFilter,
  partnerLookup,
  openBreakdown,
  // adjustments + adjustment modal
  filteredAdjustments,
  adjOpen, setAdjOpen, adjForm, setAdjForm, adjEditId,
  saveAdj, removeAdj, editAdj, addAdjFor,
  // lock actions
  startLock, unlockPartner, reconcilePartner,
}) {
  const summaryFiltered = partnerTabFilter ? data.summary.filter((s) => s.partner_id === partnerTabFilter) : data.summary;
  const byPeriodFiltered = partnerTabFilter ? data.by_period.filter((r) => r.partner_id === partnerTabFilter) : data.by_period;
  const ft = summaryFiltered.reduce((a, s) => ({
    payout: a.payout + s.total_payout, gst: a.gst + s.total_gst, tds: a.tds + s.total_tds,
    adj: a.adj + (s.total_adjustments || 0), net: a.net + s.total_net_payable,
  }), { payout: 0, gst: 0, tds: 0, adj: 0, net: 0 });

  return (
    <div className="p-4 sm:p-10 space-y-6">
      <div className="flex flex-wrap items-center gap-2" data-testid="partner-tab-filter-bar">
        <div className="w-64">
          <Select
            testid="partner-tab-partner-filter"
            placeholder="All partners"
            options={[...data.summary]
              .sort((a, b) => a.partner_id.localeCompare(b.partner_id))
              .map((s) => ({ value: s.partner_id, label: `${s.partner_id} · ${s.partner_name}` }))}
            value={partnerTabFilter}
            onChange={setPartnerTabFilter}
          />
        </div>
        {partnerTabFilter && (
          <Btn variant="ghost" testid="partner-tab-clear" onClick={() => setPartnerTabFilter("")}>Clear</Btn>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Total Commission</div>
          <div className="num text-xl font-semibold mt-1.5">₹ {fmtINR(ft.payout, 0)}</div>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">+ GST</div>
          <div className="num text-xl font-semibold mt-1.5 text-[var(--ink-2)]">₹ {fmtINR(ft.gst, 0)}</div>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">− TDS Deducted</div>
          <div className="num text-xl font-semibold mt-1.5 text-[var(--negative)]">₹ {fmtINR(ft.tds, 0)}</div>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">± Adjustments</div>
          <div className={`num text-xl font-semibold mt-1.5 ${ft.adj < 0 ? "text-[var(--negative)]" : ft.adj > 0 ? "text-[var(--positive)]" : ""}`}>{ft.adj < 0 ? "−" : ft.adj > 0 ? "+" : ""} ₹ {fmtINR(Math.abs(ft.adj), 0)}</div>
        </div>
        <div className="bg-[var(--surface)] border border-[var(--line)] p-4 ring-2 ring-[var(--accent)]">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Net Payable</div>
          <div className="num text-xl font-semibold mt-1.5 text-[var(--accent)]">₹ {fmtINR(ft.net, 0)}</div>
        </div>
      </div>

      <Section title={`Total by Partner${partnerTabFilter ? " (filtered)" : " (All Periods)"}`} testid="payouts-summary">
        <div className="overflow-auto">
          <div className="overflow-x-auto"><table className="table-data w-full">
            <thead><tr>
              <th>Partner</th>
              <th className="text-right">Commission</th>
              <th className="text-right">GST</th>
              <th className="text-right">TDS</th>
              <th className="text-right">Adjustments</th>
              <th className="text-right">Net Payable</th>
              <th className="text-right">Merchants</th>
              <th>Status</th>
            </tr></thead>
            <tbody>
              {summaryFiltered.map((s) => (
                <tr key={s.partner_id} data-testid={`payout-summary-${s.partner_id}`}>
                  <td>
                    <div className="font-medium">{s.partner_name}</div>
                    <div className="text-[11px] text-[var(--muted)] num">{s.partner_id} · TDS {(s.tds_rate * 100).toFixed(1)}%{s.gst_applicable ? ` · GST ${(s.gst_rate * 100).toFixed(0)}%` : ""}</div>
                  </td>
                  <td className="text-right num font-medium">{fmtINR(s.total_payout, 0)}</td>
                  <td className="text-right num text-[var(--ink-2)]">{s.total_gst > 0 ? `+ ${fmtINR(s.total_gst, 0)}` : <span className="text-[var(--muted)]" title="Partner is not GST-applicable">₹ 0</span>}</td>
                  <td className="text-right num text-[var(--negative)]">{s.total_tds > 0 ? `− ${fmtINR(s.total_tds, 0)}` : <span className="text-[var(--muted)]">—</span>}</td>
                  <td className={`text-right num ${(s.total_adjustments || 0) < 0 ? "text-[var(--negative)]" : (s.total_adjustments || 0) > 0 ? "text-[var(--positive)]" : "text-[var(--muted)]"}`}>{s.total_adjustments ? `${s.total_adjustments < 0 ? "−" : "+"} ${fmtINR(Math.abs(s.total_adjustments), 0)}` : "—"}</td>
                  <td className="text-right num font-semibold text-[var(--accent)]">
                    <button type="button" onClick={() => openBreakdown(s.partner_id, s.partner_name, null)} data-testid={`net-explain-summary-${s.partner_id}`} className="hover:underline focus:underline focus:outline-none">
                      {fmtINR(s.total_net_payable, 0)}
                    </button>
                  </td>
                  <td className="text-right num">{s.num_programs}</td>
                  <td><PayoutStatusBadge status={s.status} /></td>
                </tr>
              ))}
              {!summaryFiltered.length && <tr><td colSpan={8} className="text-center text-[var(--muted)] py-6">No partner data.</td></tr>}
            </tbody>
          </table></div>
        </div>
      </Section>

      <Section title={`Manual Adjustments${partnerTabFilter ? ` — ${partnerLookup[partnerTabFilter] || partnerTabFilter}` : ""}`} testid="payout-adjustments"
        right={<Btn variant="accent" testid="add-adjustment" onClick={() => addAdjFor(partnerTabFilter)}><Plus size={12} /> Add adjustment</Btn>}>
        <div className="overflow-auto max-h-[340px]">
          <div className="overflow-x-auto"><table className="table-data w-full">
            <thead><tr><th>Partner</th><th>Period</th><th>Label</th><th className="text-right">Amount</th><th>Notes</th><th></th></tr></thead>
            <tbody>
              {filteredAdjustments.map((a) => (
                <tr key={a.id} data-testid={`adjustment-row-${a.id}`} className={a.source === "standing" ? "bg-[var(--accent)]/5" : ""}>
                  <td className="text-[12px]">{partnerLookup[a.partner_id] || a.partner_id} <span className="text-[var(--muted)] num ml-1">{a.partner_id}</span></td>
                  <td className="num text-[12px]">{fmtPeriod(a.period)}</td>
                  <td className="text-[12px] font-medium">
                    {a.label}
                    {a.source === "standing" && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/30 rounded-sm">recurring</span>}
                    {a.override_of && <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-semibold bg-amber-50 text-amber-800 border border-amber-200 rounded-sm">override</span>}
                  </td>
                  <td className={`text-right num ${a.amount < 0 ? "text-[var(--negative)]" : "text-[var(--positive)]"}`}>{a.amount < 0 ? "−" : "+"} {fmtINR(Math.abs(a.amount), 0)}</td>
                  <td className="text-[11px] text-[var(--muted)] truncate max-w-xs">{a.notes || "—"}</td>
                  <td className="text-right">
                    <button onClick={() => editAdj(a)} className="p-1 hover:bg-[var(--surface-2)] rounded-sm" title={a.source === "standing" ? "Override for this month" : "Edit"}><Pencil size={12} /></button>
                    {a.source !== "standing" && (
                      <button onClick={() => removeAdj(a.id)} className="p-1 hover:bg-red-50 text-[var(--negative)] rounded-sm"><Trash2 size={12} /></button>
                    )}
                  </td>
                </tr>
              ))}
              {!filteredAdjustments.length && <tr><td colSpan={6} className="text-center text-[var(--muted)] py-4">No adjustments yet. Add one to deduct PG fees, bonuses, or route part of a partner&apos;s payout elsewhere.</td></tr>}
            </tbody>
          </table></div>
        </div>
      </Section>

      <Modal open={adjOpen} onClose={() => setAdjOpen(false)} title={adjEditId ? "Edit Adjustment" : "New Adjustment"} testid="adjustment-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Partner</label>
            <Select
              testid="adj-form-partner"
              options={(() => {
                // iter39.2 — Union of all real partners (from /api/partners) with
                // any partners currently in the payout summary. Previously we
                // only sourced from `data.summary`, so partners with no
                // calc/payout for the loaded period never appeared — you
                // couldn't add an adjustment for them. Merge + de-dupe by
                // partner_id, exclude P000, sort by id.
                const seen = new Map();
                (partners || []).forEach((p) => {
                  if (p.partner_id && p.partner_id !== "P000") {
                    seen.set(p.partner_id, p.partner_name || p.partner_id);
                  }
                });
                (data.summary || []).forEach((s) => {
                  if (s.partner_id && !seen.has(s.partner_id)) {
                    seen.set(s.partner_id, s.partner_name || s.partner_id);
                  }
                });
                return [...seen.entries()]
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([id, name]) => ({ value: id, label: `${id} · ${name}` }));
              })()}
              value={adjForm.partner_id}
              onChange={(v) => setAdjForm({ ...adjForm, partner_id: v })}
            /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Period</label>
            <MonthPicker value={adjForm.period} onChange={(v) => setAdjForm({ ...adjForm, period: v })} testid="adj-form-period" /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Label</label>
            <Input value={adjForm.label} onChange={(e) => setAdjForm({ ...adjForm, label: e.target.value })} placeholder="e.g. PG fee deduction, Routed to new entity, Bonus" /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Amount (negative = deduction, positive = bonus)</label>
            <Input type="number" step="0.01" value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} placeholder="-5000  or  2000" />
            <div className="text-[10.5px] text-[var(--muted)] mt-1">Multiple adjustments per (partner × period) supported. Each is logged separately.</div></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
            <Input value={adjForm.notes} onChange={(e) => setAdjForm({ ...adjForm, notes: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setAdjOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={saveAdj}>{adjEditId ? "Save" : "Create"}</Btn>
        </div>
      </Modal>

      <Section title="Partner's Payout by Period" testid="payouts-by-period">
        <div className="overflow-auto max-h-[500px]">
          <div className="overflow-x-auto"><table className="table-data w-full">
            <thead><tr>
              <th>Period</th><th>Partner</th>
              <th className="text-right">Commission</th>
              <th className="text-right">GST</th>
              <th className="text-right">TDS</th>
              <th className="text-right">Net Payable</th>
              <th>Status</th>
              <th className="text-right">Lock</th>
            </tr></thead>
            <tbody>
              {byPeriodFiltered.map((r) => (
                <tr key={`${r.partner_id}-${r.period}`}>
                  <td className="num font-medium">{fmtPeriod(r.period)}</td>
                  <td>{r.partner_name} <span className="text-[11px] text-[var(--muted)] num ml-1">{r.partner_id}</span></td>
                  <td className="text-right num">{fmtINR(r.payout, 0)}</td>
                  <td className="text-right num text-[var(--ink-2)]">{r.gst > 0 ? `+ ${fmtINR(r.gst, 0)}` : <span className="text-[var(--muted)]" title="Partner is not GST-applicable">₹ 0</span>}</td>
                  <td className="text-right num text-[var(--negative)]">{r.tds > 0 ? `− ${fmtINR(r.tds, 0)}` : <span className="text-[var(--muted)]" title="No TDS configured for this partner">₹ 0</span>}</td>
                  <td className="text-right num text-[var(--accent)] font-semibold">
                    <button type="button" onClick={() => openBreakdown(r.partner_id, r.partner_name, r.period)} data-testid={`net-explain-period-${r.partner_id}-${r.period}`} className="hover:underline focus:underline focus:outline-none">
                      {fmtINR(r.net_payable, 0)}
                    </button>
                    {r.status === "Drift" && (
                      <div className="text-[10px] font-normal text-amber-700 mt-0.5">
                        snapshot ₹ {fmtINR(r.locked_snapshot_net_payable, 0)}
                        {r.drift_amount != null && (
                          <span className="ml-1">({r.drift_amount > 0 ? "+" : ""}{fmtINR(r.drift_amount, 0)})</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td><PayoutStatusBadge status={r.status} /></td>
                  <td className="text-right">
                    {r.status === "Payable" && (
                      <Btn variant="ghost" testid={`lock-btn-${r.partner_id}-${r.period}`} onClick={() => startLock(r.period, r)} title={`Lock ${r.partner_name} for ${r.period}`}>
                        <Lock size={13} /> Lock
                      </Btn>
                    )}
                    {r.status === "Locked" && (
                      <Btn variant="ghost" testid={`unlock-btn-${r.partner_id}-${r.period}`} onClick={() => unlockPartner(r.period, r.partner_id, r.partner_name)} title="Unlock — discard snapshot">
                        <Unlock size={13} /> Unlock
                      </Btn>
                    )}
                    {r.status === "Drift" && (
                      <Btn variant="accent" testid={`reconcile-btn-${r.partner_id}-${r.period}`} onClick={() => reconcilePartner(r.period, r.partner_id, r.partner_name, r.drift_amount || 0)} title="Re-lock at current calculated total">
                        <RefreshCw size={13} /> Reconcile
                      </Btn>
                    )}
                  </td>
                </tr>
              ))}
              {!byPeriodFiltered.length && <tr><td colSpan={8} className="text-center text-[var(--muted)] py-6">No period rows for this partner.</td></tr>}
            </tbody>
          </table></div>
        </div>
      </Section>
    </div>
  );
}
